import ResourceModule, { type ResourceModuleConfig } from "@/pages/admin/components/ResourceModule";
import { StatusPill, type Column } from "@/pages/admin/components/AdminUI";
import { optionsFrom } from "@/lib/adminContent";
import {
  COMPLAINT_CATEGORY,
  COMPLAINT_PRIORITY,
  COMPLAINT_STATUS,
  metaFor,
  type Complaint,
} from "@/lib/adminSystem";
import { formatDate } from "@/pages/admin/format";

const options = (list: string[]) => list.map((value) => ({ value, label: value }));

const columns: Column<Complaint>[] = [
  {
    key: "subject",
    header: "Complaint",
    primary: true,
    render: (row) => (
      <div className="min-w-0">
        <p className="text-sm font-semibold text-foreground-950 whitespace-nowrap">{row.subject}</p>
        <p className="text-xs text-foreground-500 mt-0.5">
          {row.ref || "—"} · {row.category || "General"}
        </p>
      </div>
    ),
  },
  { key: "visitor", header: "Visitor", render: (row) => <span className="text-sm text-foreground-700 whitespace-nowrap">{row.visitor_name || "—"}</span> },
  { key: "priority", header: "Priority", render: (row) => <StatusPill {...metaFor(COMPLAINT_PRIORITY, row.priority)} /> },
  { key: "status", header: "Status", render: (row) => <StatusPill {...metaFor(COMPLAINT_STATUS, row.status)} /> },
  { key: "raised", header: "Raised", render: (row) => <span className="text-sm text-foreground-600 whitespace-nowrap">{formatDate(row.raised_at)}</span> },
];

const config: ResourceModuleConfig<Complaint> = {
  table: "complaints",
  orderBy: "raised_at",
  eyebrow: "Customers",
  title: "Complaints",
  subtitle: "Guest complaints and how each one was handled — category, priority, owner and resolution, tracked to closure.",
  searchPlaceholder: "Search by subject, visitor, category…",
  searchKeys: ["ref", "subject", "visitor_name", "visitor_email", "category", "against"],
  filters: [
    { key: "status", label: "Status", options: optionsFrom(COMPLAINT_STATUS) },
    { key: "priority", label: "Priority", options: optionsFrom(COMPLAINT_PRIORITY) },
    { key: "category", label: "Category", options: options(COMPLAINT_CATEGORY) },
  ],
  statusMap: COMPLAINT_STATUS,
  statusKey: "status",
  stats: (rows) => [
    { label: "Total complaints", value: rows.length, icon: "ri-emotion-unhappy-line", tone: "primary" },
    { label: "Open", value: rows.filter((row) => row.status === "open").length, icon: "ri-error-warning-line", tone: "accent" },
    { label: "In progress", value: rows.filter((row) => row.status === "in_progress").length, icon: "ri-loader-4-line", tone: "secondary" },
    { label: "Resolved", value: rows.filter((row) => row.status === "resolved" || row.status === "closed").length, icon: "ri-check-double-line", tone: "primary" },
  ],
  columns,
  fields: [
    { key: "ref", label: "Reference" },
    { key: "subject", label: "Subject" },
    { key: "visitor_name", label: "Visitor name" },
    { key: "visitor_email", label: "Visitor email" },
    { key: "category", label: "Category", type: "select", options: options(COMPLAINT_CATEGORY) },
    { key: "against", label: "Regarding" },
    { key: "priority", label: "Priority", type: "select", options: optionsFrom(COMPLAINT_PRIORITY) },
    { key: "status", label: "Status", type: "select", options: optionsFrom(COMPLAINT_STATUS) },
    { key: "raised_at", label: "Raised on", type: "date" },
    { key: "description", label: "Description", type: "textarea" },
    { key: "resolution", label: "Resolution", type: "textarea" },
  ],
  createFields: [
    { key: "ref", label: "Reference" },
    { key: "subject", label: "Subject" },
    { key: "visitor_name", label: "Visitor name" },
    { key: "visitor_email", label: "Visitor email" },
    { key: "category", label: "Category", type: "select", options: options(COMPLAINT_CATEGORY) },
    { key: "against", label: "Regarding" },
    { key: "priority", label: "Priority", type: "select", options: optionsFrom(COMPLAINT_PRIORITY) },
    { key: "description", label: "Description", type: "textarea" },
  ],
  newDefaults: {
    ref: "",
    subject: "",
    visitor_name: "",
    visitor_email: "",
    category: "Service",
    against: "",
    priority: "medium",
    status: "open",
    description: "",
    resolution: "",
  },
  emptyTitle: "No complaints yet",
  emptyMessage: "When a guest raises a concern, log it here and track it to resolution.",
  rowTitle: (row) => row.subject,
};

export default function AdminComplaints() {
  return <ResourceModule config={config} />;
}